import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Pagination,
  Stack
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Refresh
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface PayoutRule {
  id: number;
  gameId: number;
  gameName: string;
  betType: string;
  prizeTier: string;
  multiplier: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// UserPayoutRule interface removed as it's not used in this component

interface PayoutRuleData {
  counter: string;
  betTypes: {
    [key: string]: {
      price: number;
      commission: number;
      prizes: {
        [key: string]: number;
      };
      isUserSpecific?: { [key: string]: boolean }; // Track which prizes are user-specific
    };
  };
}

interface EditingCell {
  counter: string;
  betType: string;
  field: string;
  value: number;
}

const PayoutRulesDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 10 operators per page
  const [selectedCounter, setSelectedCounter] = useState<string>('all');

  // Fetch default payout rules
  const { data: payoutRules = [], isLoading: rulesLoading, error: rulesError } = useQuery({
    queryKey: ['payoutRules'],
    queryFn: async () => {
      const response = await axios.get('/admin/payout-rules');
      return response.data as PayoutRule[];
    }
  });

  // Fetch default payout rules to compare against
  const { data: defaultRules } = useQuery({
    queryKey: ['defaultPayoutRules'],
    queryFn: async () => {
      const response = await axios.get('/admin/payout-rules');
      return response.data;
    }
  });

  const loading = rulesLoading;
  const error = rulesError ? 'Failed to fetch payout rules' : null;

  // Optimized data processing with memoization
  const [payoutData, setPayoutData] = useState<PayoutRuleData[]>([]);

  // Process API data efficiently
  useEffect(() => {
    if (payoutRules.length > 0) {
      // Use Map for O(1) lookups instead of array operations
      const groupedData = new Map<string, PayoutRuleData>();
      
      // Create a map of default rule values for comparison
    const defaultRuleMap = new Map();
    if (defaultRules && Array.isArray(defaultRules)) {
      defaultRules.forEach((rule: any) => {
        const ruleKey = `${rule.betType}-${rule.prizeTier}`;
        defaultRuleMap.set(ruleKey, rule.multiplier);
      });
    }
      
      // Process rules in batches to avoid blocking UI
      const processRules = () => {
        payoutRules.forEach(rule => {
          const counter = rule.betType || 'Default BetType';
          
          if (!groupedData.has(counter)) {
            groupedData.set(counter, {
              counter,
              betTypes: {}
            });
          }
          
          const counterData = groupedData.get(counter)!;
          const betTypeKey = rule.gameName || 'Default';
          
          if (!counterData.betTypes[betTypeKey]) {
            counterData.betTypes[betTypeKey] = {
              price: 1.00,
              commission: 16.00,
              prizes: {},
              isUserSpecific: {}
            };
          }
          
          // Direct assignment instead of spread operator for better performance
          counterData.betTypes[betTypeKey].prizes[rule.prizeTier] = rule.multiplier;
          
          // Check if this rule differs from default
           const ruleKey = `${rule.betType}-${rule.prizeTier}`;
           const defaultAmount = defaultRuleMap.get(ruleKey);
           const isCustomized = defaultAmount !== undefined && rule.multiplier !== defaultAmount;
           counterData.betTypes[betTypeKey].isUserSpecific![rule.prizeTier] = isCustomized;
        });
        
        // Convert Map to Array only once
        setPayoutData(Array.from(groupedData.values()));
      };
      
      // Use requestIdleCallback for non-blocking processing
      if ('requestIdleCallback' in window) {
        requestIdleCallback(processRules);
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(processRules, 0);
      }
    }
  }, [payoutRules, defaultRules]);

  const betTypeColumns = ['Big', 'Small', '4A', 'A', 'ABC', '5D', '6D', '2A', '2F'];
  const prizeRows = ['Price', 'Prize 1', 'Prize 2', 'Prize 3', 'Starters/Prize 4', 'Consolation/Prize 5', 'Prize 6'];

  // Memoized filtered data to prevent unnecessary recalculations
  const filteredData = useMemo(() => {
    return selectedCounter === 'all' 
      ? payoutData 
      : payoutData.filter(item => item.counter === selectedCounter);
  }, [payoutData, selectedCounter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCounter]);

  const handleCellEdit = (counter: string, betType: string, field: string, currentValue: number) => {
    setEditingCell({ counter, betType, field, value: currentValue });
    setEditValue(currentValue.toString());
  };

  // Mutation for updating payout rules
  const updatePayoutRuleMutation = useMutation({
    mutationFn: async ({ counter, betType, field, value }: { counter: string; betType: string; field: string; value: number }) => {
      const response = await axios.put('/admin/payout-rules/update', {
        counter,
        betType,
        field,
        value,
        effectiveFrom: new Date().toISOString().split('T')[0],
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutRules'] });
      queryClient.invalidateQueries({ queryKey: ['userPayoutRules'] });
    }
  });

  const handleSaveEdit = async () => {
    if (!editingCell) return;
    
    try {
      const newValue = parseFloat(editValue);
      if (isNaN(newValue) || newValue <= 0) {
        alert('Please enter a valid positive number');
        return;
      }

      await updatePayoutRuleMutation.mutateAsync({
        counter: editingCell.counter,
        betType: editingCell.betType,
        field: editingCell.field,
        value: newValue
      });

      // Update local state for immediate UI feedback
      setPayoutData(prev => prev.map(counterData => {
        if (counterData.counter === editingCell.counter) {
          const updatedBetTypes = { ...counterData.betTypes };
          if (editingCell.field === 'Price') {
            // Price is always 1.00, no need to update
          } else {
            updatedBetTypes[editingCell.betType].prizes[editingCell.field] = newValue;
          }
          return { ...counterData, betTypes: updatedBetTypes };
        }
        return counterData;
      }));

      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Failed to save changes');
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const getCellValue = (counter: string, betType: string, field: string): number => {
    const counterData = payoutData.find(item => item.counter === counter);
    if (!counterData || !counterData.betTypes[betType]) return 0;
    
    if (field === 'Price') return 1.00; // Always return 1.00 for price
    return counterData.betTypes[betType].prizes[field] || 0;
  };

  const isUserSpecificRule = (counter: string, betType: string, field: string): boolean => {
    const counterData = payoutData.find(item => item.counter === counter);
    if (!counterData || !counterData.betTypes[betType]) return false;
    
    const betTypeData = counterData.betTypes[betType];
    return betTypeData.isUserSpecific?.[field] || false;
  };

  const formatValue = (value: number, field: string): string => {
    return value.toFixed(2);
  };

  const renderCell = (counter: string, betType: string, field: string) => {
    const value = getCellValue(counter, betType, field);
    const isEditing = editingCell?.counter === counter && 
                     editingCell?.betType === betType && 
                     editingCell?.field === field;
    const isSpecific = isUserSpecificRule(counter, betType, field);

    // Price field is not editable (always 1.00)
    if (field === 'Price') {
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            color: isSpecific ? 'success.main' : 'grey.500' // Green for specific, grey for default
          }}
        >
          {formatValue(value, field)}
        </Typography>
      );
    }

    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={editValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setEditValue(value);
              }
            }}
            size="small"
            type="text"
            sx={{ width: '80px' }}
            autoFocus
          />
          <IconButton size="small" onClick={handleSaveEdit} color="primary">
            <Save fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancelEdit}>
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
        onClick={() => handleCellEdit(counter, betType, field, value)}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: isSpecific ? 'success.main' : 'grey.500' // Green for specific, grey for default
          }}
        >
          {formatValue(value, field)}
        </Typography>
        <Edit fontSize="small" sx={{ opacity: 0.5, ml: 1 }} />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Payout Rules Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Counter</InputLabel>
            <Select
              value={selectedCounter}
              label="Filter by Counter"
              onChange={(e) => setSelectedCounter(e.target.value)}
            >
              <MenuItem value="all">All Counters</MenuItem>
              {payoutData.map(item => (
                <MenuItem key={item.counter} value={item.counter}>
                  {item.counter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {paginatedData.map((counterData) => (
        <Card key={counterData.counter} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white', 
              p: 2, 
              m: -2, 
              mb: 2,
              borderRadius: 1
            }}>
              {counterData.counter}
            </Typography>
            
            <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto', width: '100%' }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: '#e3f2fd', 
                      fontWeight: 'bold',
                      minWidth: 150
                    }}>
                      Details
                    </TableCell>
                    {betTypeColumns.map((betType) => (
                      <TableCell 
                        key={betType} 
                        align="center" 
                        sx={{ 
                          backgroundColor: '#e3f2fd', 
                          fontWeight: 'bold',
                          minWidth: 100
                        }}
                      >
                        {betType}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prizeRows.map((field) => {
                    // Skip rows that don't exist for certain bet types
                    const hasData = betTypeColumns.some(betType => 
                      getCellValue(counterData.counter, betType, field) > 0
                    );
                    
                    if (!hasData && field !== 'Price') {
                      return null;
                    }

                    return (
                      <TableRow key={field}>
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: field === 'Price' ? '#f5f5f5' : 'inherit'
                        }}>
                          {field}
                        </TableCell>
                        {betTypeColumns.map((betType) => {
                          const value = getCellValue(counterData.counter, betType, field);
                          
                          return (
                            <TableCell 
                              key={`${field}-${betType}`} 
                              align="center"
                              sx={{
                                backgroundColor: field === 'Price' ? '#f5f5f5' : 'inherit',
                                p: 1
                              }}
                            >
                              {value > 0 ? renderCell(counterData.counter, betType, field) : '-'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      {filteredData.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No payout rules found for the selected filter.
          </Typography>
        </Paper>
      )}

      {filteredData.length > 0 && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredData.length)} - {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} operators
          </Typography>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Box>
  );
};

export default PayoutRulesDashboard;